import { useState, useMemo } from 'react';
import { Users, Plus, Trash2, ShieldCheck, ShieldAlert, Info, AlertTriangle, Send, PenLine } from 'lucide-react';

export interface SignatoryCandidate {
  full_name: string;
  role: string;
  role_code: string;
  birth_date: string | null;
  source: 'brreg_signatory' | 'daglig_leder' | 'styreleder' | 'styret' | 'manual';
  brreg_verified: boolean;
  can_sign_alone: boolean;
  priority: number;
  email: string | null;
  phone: string | null;
  default_on: boolean;
}

export interface SignatorySelection {
  candidates: SignatoryCandidate[];
  hasBrregSignatories: boolean;
  autoHint: 'brreg_explicit_signatory' | 'daglig_leder_fallback' | 'styreleder_fallback' | 'roles_but_no_default' | 'no_brreg_roles' | string;
  allow_manual_entry: boolean;
  authority_declaration_text: string;
}

interface ChosenSignatory extends SignatoryCandidate {
  key: string;             // stabil id for React keys
  is_manual: boolean;
  is_initiator: boolean;   // markert av initiator som «jeg signerer selv»
}

interface Props {
  selection: SignatorySelection;
  currentUser: { full_name: string; email: string };
  organizationName: string;
  onCancel: () => void;
  onSubmit: (payload: {
    signatories: Array<{
      full_name: string; role: string; email: string | null; phone: string | null;
      source: string; brreg_verified: boolean; is_initiator: boolean;
    }>;
    authority_declaration_accepted: true;
    initiator_is_signer: boolean;
  }) => Promise<void>;
  submitting?: boolean;
  externalError?: string | null;
}

const HINT_MESSAGES: Record<string, { icon: React.ElementType; title: string; body: string; tone: 'info' | 'warn' }> = {
  brreg_explicit_signatory: {
    icon: ShieldCheck,
    title: 'Signaturberettigede funnet i Brønnøysund',
    body: 'Personer med formell signaturrett er registrert på organisasjonen. Vi har haket av de anbefalte — du kan haka av flere hvis flere skal signere sammen.',
    tone: 'info',
  },
  daglig_leder_fallback: {
    icon: Info,
    title: 'Ingen eksplisitt signaturrett registrert — daglig leder foreslått',
    body: 'Brønnøysund viser ingen personer med registrert signaturrett for organisasjonen. Daglig leder er haket av som standard, siden vedkommende normalt har fullmakt til daglig ledelse i kraft av sin funksjon og delegasjon fra styret. Rettsgrunnlaget varierer med organisasjonsform: for aksjeselskap gjelder aksjeloven §6-32, for samvirkeforetak samvirkelova, for stiftelser stiftelsesloven, og for foreninger og offentlige virksomheter gjelder vedtekter, reglement eller organisasjonens interne fullmaktsstruktur. Ved signering forutsetter systemet at nødvendig fullmakt er ivaretatt av administrativ ledelse — i tillegg til egenerklæringen nederst som du må bekrefte. Du kan endre valget.',
    tone: 'info',
  },
  styreleder_fallback: {
    icon: Info,
    title: 'Styreleder foreslått som signatar',
    body: 'Brønnøysund viser ingen daglig leder — styreleder er haket av som fallback. Signaturretten kan bero på vedtekter, styrevedtak eller andre fullmakter avhengig av organisasjonsform. Du kan hake av flere styremedlemmer for kollektiv signering, eller endre valget helt. Signering forutsetter at nødvendig fullmakt er ivaretatt — i tillegg til egenerklæringen nederst som du må bekrefte.',
    tone: 'info',
  },
  roles_but_no_default: {
    icon: ShieldAlert,
    title: 'Velg selv hvem som skal signere',
    body: 'Brønnøysund har rolleoppføringer, men ingen med signaturrett, daglig leder eller styreleder. Velg selv hvem som har fullmakt.',
    tone: 'warn',
  },
  no_brreg_roles: {
    icon: AlertTriangle,
    title: 'Ingen roller i Brønnøysund',
    body: 'Vi fant ingen personer registrert med relevante roller for organisasjonen i Brønnøysundregisteret. Legg til signatar(er) manuelt — vær særlig påpasselig med at valgt person faktisk har fullmakt.',
    tone: 'warn',
  },
};

export default function DpaSignatorySelector({
  selection, currentUser, organizationName, onCancel, onSubmit, submitting, externalError,
}: Props) {
  const initialChosen: ChosenSignatory[] = useMemo(
    () => selection.candidates.map((c, i) => ({
      ...c,
      key: `brreg-${i}`,
      is_manual: false,
      is_initiator: !!(currentUser.email && c.email &&
        c.email.toLowerCase() === currentUser.email.toLowerCase()),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection.candidates]
  );

  const [chosen, setChosen] = useState<ChosenSignatory[]>(initialChosen);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
    () => new Set(initialChosen.filter(c => c.default_on).map(c => c.key))
  );
  const [emails, setEmails] = useState<Record<string, string>>(
    () => Object.fromEntries(initialChosen.map(c => [c.key, c.email || '']))
  );
  const [manualName, setManualName] = useState('');
  const [manualRole, setManualRole] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  // Initiator-hake er default AV. Administrative brukere (studieforbund-admin,
  // rolle-kontoer o.l.) skal IKKE default hakes som signatar — de administrerer
  // signeringsflyten på vegne av personer med signaturrett. Hakes kun på hvis
  // innlogget bruker selv er den fysiske personen med signaturrett.
  const [initiatorIsSigner, setInitiatorIsSigner] = useState(false);
  const [initiatorRole, setInitiatorRole] = useState('');
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const addManual = () => {
    if (!manualName.trim() || !manualRole.trim()) return;
    const key = `manual-${Date.now()}`;
    const newC: ChosenSignatory = {
      full_name: manualName.trim(),
      role: manualRole.trim(),
      role_code: 'MANUAL',
      birth_date: null,
      source: 'manual',
      brreg_verified: false,
      can_sign_alone: false,
      priority: 90,
      email: manualEmail.trim() || null,
      phone: null,
      default_on: true,
      key, is_manual: true, is_initiator: false,
    };
    setChosen(prev => [...prev, newC]);
    setSelectedKeys(prev => new Set(prev).add(key));
    setEmails(prev => ({ ...prev, [key]: manualEmail.trim() }));
    setManualName(''); setManualRole(''); setManualEmail('');
  };

  const removeManual = (key: string) => {
    setChosen(prev => prev.filter(c => c.key !== key));
    setSelectedKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
  };

  const toggle = (key: string) => {
    setSelectedKeys(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const setEmailFor = (key: string, val: string) => {
    setEmails(prev => ({ ...prev, [key]: val }));
  };

  // Bygg endelig signatarliste for preview + submit
  const finalSignatories = useMemo(() => {
    const list = chosen.filter(c => selectedKeys.has(c.key)).map(c => {
      const emailVal = emails[c.key] || c.email || '';
      const isInitiator = initiatorIsSigner
        && !!currentUser.email
        && emailVal.toLowerCase() === currentUser.email.toLowerCase();
      return {
        ...c,
        email: emailVal || null,
        is_initiator: isInitiator,
      };
    });
    // Hvis initiator har valgt "Jeg signerer også" men ikke er i listen: add
    if (initiatorIsSigner && !list.some(s => s.is_initiator)) {
      list.push({
        key: 'initiator',
        full_name: currentUser.full_name,
        role: initiatorRole,
        role_code: 'INITIATOR',
        birth_date: null,
        source: 'manual',
        brreg_verified: false,
        can_sign_alone: false,
        priority: 0,
        email: currentUser.email,
        phone: null,
        default_on: true,
        is_manual: true,
        is_initiator: true,
      });
    }
    return list;
  }, [chosen, selectedKeys, emails, initiatorIsSigner, currentUser, initiatorRole]);

  const liveErrors = useMemo((): string[] => {
    const errs: string[] = [];
    if (finalSignatories.length === 0) errs.push('Minst én signatar må velges.');
    if (finalSignatories.length > 10) errs.push('Maks 10 signatarer per signeringsrunde.');
    if (!declarationAccepted) errs.push('Du må bekrefte erklæringen om fullmakt.');
    for (const s of finalSignatories) {
      if (!s.full_name?.trim()) errs.push('Alle signatarer må ha navn.');
      if (!s.is_initiator && !s.email) {
        errs.push(`${s.full_name || 'Signatar uten navn'}: e-post kreves — brukes til delegat-invitasjon.`);
      }
    }
    return Array.from(new Set(errs));
  }, [finalSignatories, declarationAccepted]);

  const canSubmit = liveErrors.length === 0 && !submitting;

  const handleSubmit = async () => {
    setErrors(liveErrors);
    if (liveErrors.length > 0) return;
    await onSubmit({
      signatories: finalSignatories.map(s => ({
        full_name: s.full_name,
        role: s.role,
        email: s.email,
        phone: s.phone,
        source: s.source,
        brreg_verified: s.brreg_verified,
        is_initiator: s.is_initiator,
      })),
      authority_declaration_accepted: true,
      initiator_is_signer: initiatorIsSigner,
    });
  };

  const hintCfg = HINT_MESSAGES[selection.autoHint] || HINT_MESSAGES.no_brreg_roles;
  const HintIcon = hintCfg.icon;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Users className="h-5 w-5" /> Velg hvem som skal signere
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Signering på vegne av <strong>{organizationName}</strong>.
          Alle valgte personer må fullføre Vipps-verifisering for at avtalen skal bli aktiv.
        </p>
      </header>

      {/* Info-boks basert på Brreg-status */}
      <div className={`rounded-lg border p-4 flex gap-3 ${
        hintCfg.tone === 'info' ? 'bg-kf-blue-50 border-kf-blue-200 text-kf-blue-900' : 'bg-amber-50 border-amber-200 text-amber-900'
      }`}>
        <HintIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${hintCfg.tone === 'info' ? 'text-kf-blue-600' : 'text-amber-600'}`} />
        <div>
          <div className="font-semibold text-sm">{hintCfg.title}</div>
          <div className="text-sm mt-1">{hintCfg.body}</div>
        </div>
      </div>

      {/* Kandidat-liste */}
      {chosen.length > 0 && (
        <div className="space-y-2">
          {chosen.map(c => {
            const selected = selectedKeys.has(c.key);
            return (
              <div key={c.key} className={`border rounded-md p-3 flex items-start gap-3 ${
                selected ? 'border-kf-primary bg-kf-blue-50' : 'border-gray-200'
              }`}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggle(c.key)}
                  className="mt-1 h-4 w-4"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{c.full_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">{c.role}</span>
                    {c.brreg_verified && (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 inline-flex items-center gap-1">
                        <ShieldCheck className="h-3 w-3" /> Brreg-verifisert
                      </span>
                    )}
                    {c.is_manual && (
                      <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">manuelt lagt til</span>
                    )}
                  </div>
                  {selected && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <label className="text-xs text-gray-600">E-post:</label>
                      <input
                        type="email"
                        value={emails[c.key] || ''}
                        onChange={e => setEmailFor(c.key, e.target.value)}
                        placeholder={
                          currentUser.email && emails[c.key]?.toLowerCase() === currentUser.email.toLowerCase()
                            ? 'Din innloggede e-post'
                            : 'signatar@organisasjon.no (kreves for delegat-e-post)'
                        }
                        className="text-sm border border-gray-300 rounded px-2 py-1 flex-1 min-w-[220px]"
                      />
                      {c.is_manual && (
                        <button type="button" onClick={() => removeManual(c.key)}
                          className="text-sm text-red-600 hover:text-red-800 inline-flex items-center gap-1">
                          <Trash2 className="h-3 w-3" /> Fjern
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Legg til manuell signatar */}
      {selection.allow_manual_entry && (
        <details className="border border-dashed border-gray-300 rounded-md p-4">
          <summary className="cursor-pointer text-sm text-gray-700 font-medium inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> Legg til signatar manuelt
          </summary>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={manualName} onChange={e => setManualName(e.target.value)}
              placeholder="Fullt navn" className="text-sm border border-gray-300 rounded px-2 py-1.5" />
            <input value={manualRole} onChange={e => setManualRole(e.target.value)}
              placeholder="Rolle (f.eks. Styreleder)" className="text-sm border border-gray-300 rounded px-2 py-1.5" />
            <input value={manualEmail} onChange={e => setManualEmail(e.target.value)}
              placeholder="E-post" type="email" className="text-sm border border-gray-300 rounded px-2 py-1.5" />
          </div>
          <button type="button" onClick={addManual}
            disabled={!manualName.trim() || !manualRole.trim()}
            className="mt-3 px-3 py-1.5 bg-kf-primary text-white text-sm rounded hover:bg-kf-dark disabled:opacity-50">
            Legg til
          </button>
        </details>
      )}

      {/* Initiator som signer — kun hvis admin selv har signaturrett */}
      <div className="border-t border-gray-200 pt-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={initiatorIsSigner}
            onChange={e => setInitiatorIsSigner(e.target.checked)} className="mt-1 h-4 w-4" />
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">
              Jeg ({currentUser.full_name}) er en av signatarene og signerer selv med Vipps
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Hakes kun på hvis <strong>du personlig</strong> har signaturrett for {organizationName} og
              har din personlige Vipps klar. Administrative brukere som organiserer signeringen på vegne
              av andre skal la denne stå av — de valgte personene får e-postlenke og signerer via Vipps selv.
            </div>
            {initiatorIsSigner && (
              <div className="mt-2 flex items-center gap-2">
                <label className="text-xs text-gray-600">Din rolle i organisasjonen:</label>
                <input value={initiatorRole} onChange={e => setInitiatorRole(e.target.value)}
                  placeholder="F.eks. Daglig leder, Styreleder"
                  className="text-sm border border-gray-300 rounded px-2 py-1 flex-1 max-w-xs" />
              </div>
            )}
          </div>
        </label>
      </div>

      {/* Forhåndsvisning av signatur-blokken */}
      <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
          <PenLine className="h-3 w-3" /> Slik vises signatur-blokken i den signerte avtalen
        </div>
        {finalSignatories.length === 0 ? (
          <div className="text-sm text-gray-500 italic">Ingen signatarer valgt ennå.</div>
        ) : finalSignatories.length === 1 ? (
          <div className="text-sm text-gray-800">
            <strong>Kontakt:</strong> {finalSignatories[0].full_name}
            {finalSignatories[0].email && ` (${finalSignatories[0].email})`} — {finalSignatories[0].role}
            {finalSignatories[0].brreg_verified && <em className="text-gray-500"> (signaturrett verifisert i Brønnøysund)</em>}
          </div>
        ) : (
          <div className="text-sm text-gray-800">
            <strong>Signatarer:</strong>
            <ul className="mt-1 ml-4 list-disc space-y-1">
              {finalSignatories.map((s, i) => (
                <li key={i}>
                  <strong>{s.full_name}</strong>{s.email && ` (${s.email})`} — {s.role}
                  {s.brreg_verified && <em className="text-gray-500"> — signaturrett verifisert i Brønnøysund</em>}
                  {s.is_initiator && <span className="ml-2 text-xs text-kf-primary">(du signerer først)</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Erklæring */}
      <div className="border-2 border-amber-300 bg-amber-50 rounded-md p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={declarationAccepted}
            onChange={e => setDeclarationAccepted(e.target.checked)} className="mt-1 h-4 w-4" />
          <div className="text-sm text-gray-900">
            <div className="font-semibold text-amber-900 mb-1">Erklæring om fullmakt (obligatorisk)</div>
            {selection.authority_declaration_text}
          </div>
        </label>
      </div>

      {externalError && (
        <div className="border-2 border-red-400 bg-red-50 rounded-md p-3 text-sm text-red-900">
          <div className="font-semibold mb-1 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Feil ved oppstart av signering:
          </div>
          <div className="whitespace-pre-line">{externalError}</div>
        </div>
      )}
      {(errors.length > 0 || liveErrors.length > 0) && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm text-amber-900">
          <div className="font-semibold mb-1 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Rett opp følgende før du kan starte signering:
          </div>
          <ul className="ml-6 list-disc">
            {(errors.length > 0 ? errors : liveErrors).map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="flex gap-3 justify-end items-center">
        {canSubmit && (
          <span className="text-xs text-green-700 inline-flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" /> Klar for signering
          </span>
        )}
        <button type="button" onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
          Avbryt
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          title={liveErrors.length > 0 ? liveErrors.join(' • ') : ''}
          className="px-4 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-dark inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="h-4 w-4" />
          {submitting ? 'Starter signering…' : (
            finalSignatories.some(s => s.is_initiator)
              ? 'Start min Vipps-signering'
              : (finalSignatories.length === 1 ? 'Send signeringslenke' : 'Send signeringslenker')
          )}
        </button>
      </div>
    </div>
  );
}
